/**
 * DisplaySettingsPanel — user-facing display settings popover ("Visning").
 *
 * Presentational component: prefs + callbacks in, UI out.
 * No data fetching, no auth logic, no privileged strings.
 * Safe for the public, sessionless register bundle.
 *
 * Props:
 *   prefs      — fully-resolved current DisplayPreferences
 *   onChange   — called with (key, value) when a control changes
 *   onReset    — called with the resettable keys when the user clicks
 *                "Tilbakestill til standard"
 *   onClose    — called when the user closes the panel
 *   showTheme / showAccent / showFont — per-control visibility, all default
 *                true. A surface with a fixed theme, palette, or typeface hides
 *                the control it cannot honour; reset then skips that key too.
 *
 * Labels via useT() (display.* i18n keys).
 * Styling via .disp / .disp-* classes from shared/styles/components.css.
 *
 * a11y:
 *   - Segmented controls use named groups with native radio inputs
 *   - Toggle switches use Designsystemet's native checked inputs and labels
 *   - Close button has an accessible label from i18n
 *   - :focus-visible outlines provided by components.css
 *   - Keyboard operable: buttons are native <button> elements
 *   - On open, focus moves into the dialog; Escape and an outside-click
 *     backdrop both dismiss it; focus is restored to the trigger on close
 */
import React from 'react'
import { Button, Heading, Paragraph, Switch, ViewSwitch } from '../components'
import { useT } from '../i18n'
import { type DisplayPreferences, DEFAULTS, ALLOWED_ACCENTS, colorIntensityFilter, type Accent, type Font, type ThemeMode, type TextSize, type Density, type Crisp } from '../theme/displayPreferences'

// ---------------------------------------------------------------------------
// Font stack strings for the option previews. The default face follows the
// brand (the admin bridge points it at Inter), so its preview must too —
// a swatch rendering a face the choice does not apply would lie.
// ---------------------------------------------------------------------------

const FONT_STACKS: Record<Font, string> = {
  source:   'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif',
  plex:     '"IBM Plex Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif',
  atkinson: '"Atkinson Hyperlegible",-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif',
  system:   '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,system-ui,sans-serif',
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Like the pane resizers: preview locally, persist once the native change ends. */
function ColorIntensitySlider({ value, onCommit }: { value: number; onCommit: (value: number) => void }) {
  const t = useT()
  const id = React.useId()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [live, setLive] = React.useState(value)
  React.useEffect(() => {
    setLive(value)
    return () => {
      document.documentElement.style.setProperty('--display-color-filter', colorIntensityFilter(value))
    }
  }, [value])
  React.useEffect(() => {
    const input = inputRef.current!
    // React's onChange follows input events for a range. Native change fires on
    // release (or each keyboard adjustment), avoiding a PATCH for every pixel.
    const commit = () => onCommit(input.valueAsNumber)
    input.addEventListener('change', commit)
    return () => input.removeEventListener('change', commit)
  }, [onCommit])
  return (
    <div className="disp-intensity">
      <div className="disp-intensity__label">
        <label htmlFor={id}>{t('display.color_intensity.label')}</label>
        <output htmlFor={id}>{live} %</output>
      </div>
      <input
        ref={inputRef}
        id={id}
        type="range"
        min={0}
        max={100}
        step={1}
        value={live}
        aria-valuetext={`${live} %`}
        onChange={event => {
          const next = event.currentTarget.valueAsNumber
          setLive(next)
          document.documentElement.style.setProperty('--display-color-filter', colorIntensityFilter(next))
        }}
      />
    </div>
  )
}

/** A labelled row with optional hint text. */
function DispRow({
  label,
  hint,
  hintId,
  children,
  stack = false,
}: {
  label: string
  hint?: string
  hintId?: string
  children: React.ReactNode
  stack?: boolean
}) {
  return (
    <div className={`disp-row${stack ? ' disp-row--stack' : ''}`}>
      <div className="disp-row__l">
        <Paragraph data-size="sm" className="disp-row__label"><strong>{label}</strong></Paragraph>
        {hint && <Paragraph id={hintId} data-size="sm" className="disp-row__hint">{hint}</Paragraph>}
      </div>
      <div className="disp-row__c">{children}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type DisplaySettingsPanelProps = {
  /** Fully-resolved current preferences. */
  prefs: DisplayPreferences
  /** Called with (key, value) when a control changes. */
  onChange: (key: keyof DisplayPreferences, value: string | number) => void
  /**
   * Called when the user resets to defaults, with the keys this panel is
   * willing to reset — every preference except the ones whose control the
   * surface hid. A caller that resets everything may ignore the argument.
   */
  onReset: (keys: Array<keyof DisplayPreferences>) => void
  /** Called when the panel is closed. */
  onClose: () => void
  /** Show the light/dark/system control. Hide it on a fixed-theme surface. */
  showTheme?: boolean
  /** Show the accent swatches. Hide them on a fixed-palette surface. */
  showAccent?: boolean
  /** Show the typeface picker. Hide it where the typeface is mandated. */
  showFont?: boolean
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

/**
 * Display settings popover. Wire prefs and callbacks from useDisplayPreferences().
 *
 * Renders its own click-catching .popover-backdrop (dismiss on outside click)
 * plus the .disp dialog. The parent must place this inside a .popover-wrap
 * (position:relative) anchor — e.g. Topbar wraps the gear button + this panel —
 * so the panel's absolute positioning resolves to the trigger, not the viewport.
 * On open, focus moves into the dialog and is restored to the trigger on close.
 */
export function DisplaySettingsPanel({
  prefs,
  onChange,
  onReset,
  onClose,
  showTheme = true,
  showAccent = true,
  showFont = true,
}: DisplaySettingsPanelProps) {
  const t = useT()
  const dialogRef = React.useRef<HTMLDivElement>(null)
  const id = React.useId()

  // Move focus into the dialog on open so the Escape handler is reachable and
  // keyboard users land inside the popover; restore focus to the trigger on close.
  React.useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()
    return () => {
      previouslyFocused?.focus?.()
    }
  }, [])

  React.useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [onClose])

  // Keys the reset button writes back: everything except the ones whose
  // control this surface hides. A hidden preference is inert here, so
  // resetting it would be an invisible change — and counting it below would
  // leave the button permanently enabled with nothing left to do. Keys with no
  // control of their own (the sidebar ones) stay in: reset has always written
  // them, and they are the only way to undo them.
  const hidden: Array<keyof DisplayPreferences> = []
  if (!showTheme) hidden.push('theme')
  if (!showAccent) hidden.push('accent', 'colorIntensity')
  if (!showFont) hidden.push('font')
  const resettableKeys = (Object.keys(DEFAULTS) as Array<keyof DisplayPreferences>).filter(
    (key) => !hidden.includes(key),
  )

  // Everything resettable already at its default? Then disable the button.
  const isDefault = resettableKeys.every((key) => prefs[key] === DEFAULTS[key])

  // ── Theme mode options ──────────────────────────────────────────────────
  const themeOptions: Array<{ value: ThemeMode; label: string }> = [
    { value: 'light',  label: t('display.theme.options.light') },
    { value: 'dark',   label: t('display.theme.options.dark') },
    { value: 'system', label: t('display.theme.options.system') },
  ]

  // ── Text size options ───────────────────────────────────────────────────
  const textSizeOptions: Array<{ value: TextSize; label: string }> = [
    { value: 'sm', label: t('display.text_size.options.sm') },
    { value: 'md', label: t('display.text_size.options.md') },
    { value: 'lg', label: t('display.text_size.options.lg') },
  ]

  // ── Density options ─────────────────────────────────────────────────────
  const densityOptions: Array<{ value: Density; label: string }> = [
    { value: 'compact', label: t('display.density.options.compact') },
    { value: 'normal',  label: t('display.density.options.normal') },
    { value: 'comfy',   label: t('display.density.options.comfy') },
  ]

  // ── Crisp options ───────────────────────────────────────────────────────
  const crispOptions: Array<{ value: Crisp; label: string }> = [
    { value: 'off', label: t('display.crisp.options.false') },
    { value: 'on',  label: t('display.crisp.options.true') },
  ]

  return (
    <>
      {/* Click-catching backdrop — dismisses the popover on outside click.
          .popover-backdrop is position:fixed inset:0, below the panel's
          z-index so the panel itself stays interactive. */}
      <div className="popover-backdrop" onClick={onClose} aria-hidden="true" />

      <div
        ref={dialogRef}
        className="disp"
        role="dialog"
        aria-label={t('display.panel.title')}
        tabIndex={-1}
        // Stop clicks inside the panel from bubbling to ancestor handlers.
        onClick={(e) => e.stopPropagation()}
        // Escape dismisses the dialog (standard dialog keyboard pattern).
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !e.defaultPrevented) {
            e.stopPropagation()
            onClose()
          }
        }}
      >
        {/* Header */}
        <div className="disp__hd">
          <div>
            <Heading level={2} data-size="xs">{t('display.panel.title')}</Heading>
            <Paragraph data-size="sm" className="disp__sub">
              {showTheme || showAccent || showFont
                ? t('display.panel.subtitle')
                : t('display.panel.subtitle_readability')}
            </Paragraph>
          </div>
          <Button
            variant="ghost"
            icon="x"
            iconOnly
            onClick={onClose}
            type="button"
            aria-label={t('chrome.actions.close')}
          />
        </div>

        {/* Body */}
        <div className="disp__body">

          {/* ── Section: Tema ─────────────────────────────────────────────── */}
          {/* The heading goes with its controls — a surface that hides both
              theme and accent would otherwise show an empty section. */}
          {(showTheme || showAccent) && (
            <Heading level={3} data-size="2xs" className="disp__sec">{t('display.section.theme')}</Heading>
          )}

          {showTheme && (
            <DispRow label={t('display.theme.label')} stack>
              <ViewSwitch
                value={prefs.theme}
                options={themeOptions}
                onChange={(v) => onChange('theme', v)}
                ariaLabel={t('display.theme.label')}
              />
            </DispRow>
          )}

          {showAccent && (
            <DispRow
              label={t('display.accent.label')}
              hint={t('display.accent.hint')}
              hintId={`${id}-accent`}
              stack
            >
              <div className="disp-sw" role="group" aria-label={t('display.accent.label')} aria-describedby={`${id}-accent`}>
                {(ALLOWED_ACCENTS as readonly Accent[]).map((key) => (
                  <Button
                    key={key}
                    className={`disp-sw__b${prefs.accent === key ? ' is-on' : ''}`}
                    variant={prefs.accent === key ? 'primary' : 'default'}
                    data-accent={key}
                    onClick={() => onChange('accent', key)}
                    type="button"
                    aria-pressed={prefs.accent === key}
                  >
                    <span className="disp-sw__preview" aria-hidden="true">
                      <span className="disp-sw__nav" />
                      <span className="disp-sw__page">
                        <span className="disp-sw__chip" />
                        <span className="disp-sw__card" />
                      </span>
                    </span>
                    <span className="disp-sw__name">
                      {t(`display.accent.options.${key}` as `display.accent.options.${Accent}`)}
                      <span className="disp-sw__check" aria-hidden="true">{prefs.accent === key ? '✓' : ''}</span>
                    </span>
                  </Button>
                ))}
              </div>
            </DispRow>
          )}

          {showAccent && <ColorIntensitySlider value={prefs.colorIntensity} onCommit={value => onChange('colorIntensity', value)} />}

          {/* ── Section: Skrift ───────────────────────────────────────────── */}
          {/* Text size and density keep this section alive even without the
              typeface picker. */}
          <Heading level={3} data-size="2xs" className="disp__sec">{t('display.section.font')}</Heading>

          {showFont && (
            <DispRow label={t('display.font.label')} stack>
              <div className="disp-font" role="group" aria-label={t('display.font.label')}>
                {(['source', 'plex', 'atkinson', 'system'] as Font[]).map((key) => (
                  <Button
                    key={key}
                    className={`disp-font__b${prefs.font === key ? ' is-on' : ''}`}
                    variant={prefs.font === key ? 'primary' : 'default'}
                    onClick={() => onChange('font', key)}
                    type="button"
                    aria-pressed={prefs.font === key}
                  >
                    <span className="disp-font__sample" style={{ fontFamily: FONT_STACKS[key] }} aria-hidden="true">Ag</span>
                    <span className="disp-font__meta">
                      <span className="disp-font__name">{t(`display.font.options.${key}.name` as `display.font.options.${Font}.name`)}</span>
                      <span className="disp-font__note">{t(`display.font.options.${key}.note` as `display.font.options.${Font}.note`)}</span>
                    </span>
                    <span className="disp-sw__check" aria-hidden="true">{prefs.font === key ? '✓' : ''}</span>
                  </Button>
                ))}
              </div>
            </DispRow>
          )}

          <DispRow
            label={t('display.text_size.label')}
            hint={t('display.text_size.hint')}
            hintId={`${id}-size`}
            stack
          >
            <ViewSwitch
              value={prefs.textSize}
              options={textSizeOptions}
              onChange={(v) => onChange('textSize', v)}
              ariaLabel={t('display.text_size.label')}
              ariaDescribedBy={`${id}-size`}
            />
          </DispRow>

          <DispRow
            label={t('display.density.label')}
            hint={t('display.density.hint')}
            hintId={`${id}-density`}
            stack
          >
            <ViewSwitch
              value={prefs.density}
              options={densityOptions}
              onChange={(v) => onChange('density', v)}
              ariaLabel={t('display.density.label')}
              ariaDescribedBy={`${id}-density`}
            />
          </DispRow>

          {/* ── Section: Lesbarhet ────────────────────────────────────────── */}
          <Heading level={3} data-size="2xs" className="disp__sec">{t('display.section.readability')}</Heading>

          <DispRow
            label={t('display.crisp.label')}
            hint={prefs.crisp === 'on' ? t('display.crisp.hint_on') : t('display.crisp.hint_off')}
            hintId={`${id}-crisp`}
            stack
          >
            <ViewSwitch
              value={prefs.crisp}
              options={crispOptions}
              onChange={(v) => onChange('crisp', v)}
              ariaLabel={t('display.crisp.label')}
              ariaDescribedBy={`${id}-crisp`}
            />
          </DispRow>

          <Switch
            label={<strong>{t('display.contrast.label')}</strong>}
            description={t('display.contrast.hint')}
            checked={prefs.contrast === 'high'}
            position="end"
            onChange={event => onChange('contrast', event.target.checked ? 'high' : 'normal')}
          />

          <Switch
            label={<strong>{t('display.motion.label')}</strong>}
            description={t('display.motion.hint')}
            checked={prefs.motion === 'reduced'}
            position="end"
            onChange={event => onChange('motion', event.target.checked ? 'reduced' : 'full')}
          />

        </div>

        {/* Footer */}
        <div className="disp__ft">
          <Button
            variant="ghost"
            icon="refresh"
            onClick={() => onReset(resettableKeys)}
            disabled={isDefault}
            type="button"
          >
            {t('display.reset.label')}
          </Button>
        </div>
      </div>
    </>
  )
}
